import { useEffect } from "react";

interface SeoOptions {
  title: string;
  description: string;
  keywords?: string;
  path?: string;
  image?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function canonicalUrl(path: string): string {
  const basePath = __BASE_PATH__.replace(/\/$/, "");
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${window.location.origin}${basePath}${clean}`;
}

export function useSeo({
  title,
  description,
  keywords,
  path = "/",
  image,
  jsonLd,
}: SeoOptions) {
  useEffect(() => {
    const url = canonicalUrl(path);
    document.documentElement.lang = "zh-CN";
    document.title = title;

    setMeta("name", "description", description);
    if (keywords) setMeta("name", "keywords", keywords);
    setMeta("name", "robots", "index, follow, max-image-preview:large");
    setMeta("name", "author", "瓜网编辑部");
    setMeta("name", "theme-color", "#c9761f");

    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "瓜网");
    setMeta("property", "og:locale", "zh_CN");
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    if (image) setMeta("property", "og:image", image);

    setMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    if (image) setMeta("name", "twitter:image", image);

    setLink("canonical", url);

    const scriptId = "gw-page-jsonld";
    const prev = document.getElementById(scriptId);
    if (prev) prev.remove();
    if (jsonLd) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, path, image, jsonLd]);
}